import Checkbox from 'components/CheckBox';
import SelectDropdown from 'components/SelectDropdown';
import { useFormikContext } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OfferType, OfferEligibilityType } from 'legacy/events/types';
import { v4 as uuid } from 'uuid';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import UserSelectionModal from './components/UserSelectionModal';
import OrganizationSelectionModal from './components/OrganizationSelectionModal';
import AssociationSelectionModal from './components/AssociationSelectionModal';
import ItemSelection from './components/ItemSelection';
import { Item } from './components/ItemList';

const OfferEligibilitySelection = () => {
  const { t } = useTranslation(['events']);
  const { values, setFieldValue } = useFormikContext<OfferType>();
  const offerEligibility = values.eligibilityCriteria;
  const contracts = values.contracts;
  const [userSelectionModal, setUserSelectionModal] = useState({
    id: '',
    show: false
  });
  const [organizationSelectionModal, setOrganizationSelectionModal] = useState({
    id: '',
    show: false
  });
  const [associationSelectionModal, setAssociationSelectionModal] = useState({
    id: '',
    show: false
  });

  const handleUserSelectionModalClose = () => {
    setUserSelectionModal({
      id: '',
      show: false
    });
  };

  const handleOrganizationSelectionModalClose = () => {
    setOrganizationSelectionModal({
      id: '',
      show: false
    });
  };

  const handleAssociationSelectionModalClose = () => {
    setAssociationSelectionModal({
      id: '',
      show: false
    });
  };

  const handleItemSelectionModalSubmit = (
    items: Item[],
    eligibilityId: string,
    closeModal: () => void
  ) => {
    const newEligibility = [...offerEligibility];
    const index = newEligibility.findIndex(
      (eligibility) => eligibility.id === eligibilityId
    );
    newEligibility[index].items = items;
    setFieldValue('eligibilityCriteria', newEligibility);
    closeModal();
  };

  const handleMustHaveTicketChange = (id: string) => {
    const newUserSelection = [...offerEligibility];
    const index = newUserSelection.findIndex(
      (userSelection) => userSelection.id === id
    );
    newUserSelection[index].mustHaveTicket =
      !newUserSelection[index].mustHaveTicket;
    setFieldValue('eligibilityCriteria', newUserSelection);
  };

  const handleRemoveEligibility = (id: string) => {
    const newUserSelection = [...offerEligibility];
    const index = newUserSelection.findIndex(
      (userSelection) => userSelection.id === id
    );
    newUserSelection.splice(index, 1);
    setFieldValue('eligibilityCriteria', newUserSelection);
  };

  const handleEligibilityChange = (
    id: string,
    selector: OfferEligibilityType
  ) => {
    const newUserSelection = [...offerEligibility];
    const index = newUserSelection.findIndex(
      (userSelection) => userSelection.id === id
    );
    newUserSelection[index].selector = selector;
    setFieldValue('eligibilityCriteria', newUserSelection);
  };

  const offerEligibilityOptions = [
    {
      value: OfferEligibilityType.USERS,
      label: t('settings.promoterOffers.eligibility.individualBounceUser')
    },
    {
      value: OfferEligibilityType.ORGANIZATIONS,
      label: t('settings.promoterOffers.eligibility.organizationMembers')
    },
    {
      value: OfferEligibilityType.ASSOCIATIONS,
      label: t('settings.promoterOffers.eligibility.associationMembers')
    }
  ];

  return (
    <>
      <div className="flex flex-col mt-6">
        <div className="text-[16px] font-bold text-[#202020]">
          {t('settings.promoterOffers.eligibility.whoIsEligible')}
        </div>
        {offerEligibility.map((eligibility, index) => (
          <FadeIn key={eligibility.id}>
            <div className="mt-4">
              <SelectDropdown
                onChange={(value) => {
                  handleEligibilityChange(
                    eligibility.id,
                    value as OfferEligibilityType
                  );
                }}
                value={eligibility.selector}
                textValueStyle={dropdownStyle}
                controlStyle={controlStyle}
                data={offerEligibilityOptions}
              />
              {eligibility.selector === OfferEligibilityType.USERS && (
                <ItemSelection
                  eligibility={eligibility}
                  setItemSelectionModal={setUserSelectionModal}
                  index={index}
                  placeholder={t(
                    'settings.promoterOffers.eligibility.selectBounceUsers'
                  )}
                />
              )}
              {eligibility.selector === OfferEligibilityType.ORGANIZATIONS && (
                <ItemSelection
                  eligibility={eligibility}
                  setItemSelectionModal={setOrganizationSelectionModal}
                  index={index}
                  placeholder={t(
                    'settings.promoterOffers.eligibility.selectBounceOrganizations'
                  )}
                />
              )}
              {eligibility.selector === OfferEligibilityType.ASSOCIATIONS && (
                <ItemSelection
                  eligibility={eligibility}
                  setItemSelectionModal={setAssociationSelectionModal}
                  index={index}
                  placeholder={t(
                    'settings.promoterOffers.eligibility.selectBounceAssociations'
                  )}
                />
              )}
              <div className="flex flex-row mt-2 justify-between">
                <div className="text-sm font-bold text-[#575757]">
                  {t('settings.promoterOffers.eligibility.mustHaveTicket')}
                </div>
                <Checkbox
                  checked={eligibility.mustHaveTicket}
                  onChange={() => handleMustHaveTicketChange(eligibility.id)}
                />
              </div>
              {offerEligibility?.length === 1 && (
                <div className="w-full border-b-1 border-gray-200 py-2" />
              )}
              {offerEligibility?.length > 1 && (
                <div className="mt-4 flex items-center">
                  <div className="w-full border-b-1 border-gray-200" />
                  <button
                    type="button"
                    className="pl-4 text-red-700 text-sm"
                    onClick={() => handleRemoveEligibility(eligibility.id)}
                  >
                    {t('settings.promoterOffers.eligibility.remove')}
                  </button>
                </div>
              )}
              <button
                type="button"
                className="mt-3 text-[#3853C4] font-bold text-sm"
                onClick={() => {
                  setFieldValue('eligibilityCriteria', [
                    ...offerEligibility,
                    {
                      id: uuid(),
                      selector: OfferEligibilityType.USERS,
                      mustHaveTicket: false,
                      items: []
                    }
                  ]);
                }}
              >
                {t('settings.promoterOffers.eligibility.addMoreUsers')}
              </button>
            </div>
          </FadeIn>
        ))}
        {userSelectionModal.show && (
          <UserSelectionModal
            contracts={contracts}
            users={
              offerEligibility.find(
                (eligibility) => eligibility.id === userSelectionModal.id
              )?.items || []
            }
            onClose={handleUserSelectionModalClose}
            onSubmit={(items) =>
              handleItemSelectionModalSubmit(
                items,
                userSelectionModal.id,
                handleUserSelectionModalClose
              )
            }
          />
        )}
        {organizationSelectionModal.show && (
          <OrganizationSelectionModal
            organizations={
              offerEligibility.find(
                (eligibility) =>
                  eligibility.id === organizationSelectionModal.id
              )?.items || []
            }
            onClose={handleOrganizationSelectionModalClose}
            onSubmit={(items) =>
              handleItemSelectionModalSubmit(
                items,
                organizationSelectionModal.id,
                handleOrganizationSelectionModalClose
              )
            }
          />
        )}
        {associationSelectionModal.show && (
          <AssociationSelectionModal
            associations={
              offerEligibility.find(
                (eligibility) => eligibility.id === associationSelectionModal.id
              )?.items || []
            }
            onClose={handleAssociationSelectionModalClose}
            onSubmit={(items) =>
              handleItemSelectionModalSubmit(
                items,
                associationSelectionModal.id,
                handleAssociationSelectionModalClose
              )
            }
          />
        )}
      </div>
    </>
  );
};

const dropdownStyle = {
  color: '#202020',
  fontWeight: 400,
  fontSize: 14
};

const controlStyle = {
  borderColor: '#DCDCDC'
};

export default OfferEligibilitySelection;
